import { observable, action, flow, set, toJS } from "mobx";
import React from "react";
import {
	getProject,
	delay,
	registerHandler,
	saveSchedule,
	deleteSchedule,
	resetSchedule,
	startProject,
	stopProject,
	savePrivateProject,
	getPrivateNetworkSiteStatusForProject
} from "services/api";

import uuidv1 from "uuid/v1";
import LZString from "lz-string";
import { message } from "antd";
import _ from "lodash";
import { encrypt } from "common/abc";

export default class ProjectStore {
	@observable
	project = {
		lastError: null
	};

	@observable
	siblings = [];
	@observable
	sites = [];
	@observable
	uuid = "";
	@observable
	campaigns = [];
	@observable
	urlLists = [];
	@observable
	contentGroups = [];

	@observable
	ui = {
		loadingProject: true,
		editing: false,
		startingProject: false,
		stoppingProject: false,
		savingProject: false,
		addEditScheduleDialogVisible: false,
		savingSchedule: false,
		savingProject: false,
		deletingSchedule: false,
		resettingSchedule: false,
		updatedSite: null,
		addEditLinkSetProfileDialogVisible: false,
		linkSetProfile: null,
		loadingPrivateSiteStatus: false
	};

	@observable
	stateForm = {};

	backedUpSites = [];

	getInitUI = () => {
		return {
			loadingProject: false,
			editing: false,
			startingProject: false,
			stoppingProject: false,
			savingProject: false,
			addEditScheduleDialogVisible: false,
			savingSchedule: false,
			savingProject: false,
			deletingSchedule: false,
			resettingSchedule: false,
			updatedSite: null,
			addEditLinkSetProfileDialogVisible: false,
			linkSetProfile: null,
			loadingPrivateSiteStatus: false
		};
	};

	customizer = (objValue, srcValue, key, object, source, stack) => {
		if (srcValue == undefined) {
			return null;
		}
		if (key === "value" && _.isArray(srcValue)) {
			return srcValue;
		}
	};

	@action
	formChanged = fields => {
		if ("profileId" in fields) {
			//Preventing fetching private network twice
			if (fields.profileId.validating == false) {
				let profileIdChanged = this.stateForm.profileId.value != fields.profileId.value;
				_.mergeWith(this.stateForm, fields, this.customizer);
				if (profileIdChanged) {
					this.fetchPrivateNetwork(
						this.project.id,
						this.stateForm.profileId.value
					);
				}
			}
		} else {
			_.mergeWith(this.stateForm, fields, this.customizer);
		}
	};

	fetchPrivateNetwork = flow(function*(projectId, privateGroupId) {
		this.ui.loadingPrivateSiteStatus = true;
		const response = yield getPrivateNetworkSiteStatusForProject({
			projectId: projectId,
			privateGroupId: privateGroupId
		});
		this.ui.loadingPrivateSiteStatus = false;
		if (response == null) {
			message.error("Failed to get the private network. Unknown Error");
			return;
		}
		if (response.error) {
			message.error(response.error.message);
			return;
		}
		this.sites = observable.array(response.sites, { deep: false });
	});

	@action
	startEditingProject = () => {
		this.ui.editing = true;
		this.backedUpSites = _.cloneDeep(this.sites.slice());
	};

	@action
	cancelEditingProject = () => {
		this.ui.editing = false;
		this.sites = observable.array(this.backedUpSites, { deep: false });
		this.stateForm = this.createFormObject(this.project);
		this.backedUpSites = null;
	};

	getRawObjectFromFormObject = formObject => {
		function customizer(value) {
			if (_.isObject(value)) {
				if ("value" in value) {
					return value.value;
				}
			}
		}
		let rawJS = toJS(formObject);
		return _.cloneDeepWith(rawJS, customizer);
	};

	saveProject = flow(function*(sites) {
		this.ui.savingProject = true;
		this.backedUpSites = null;

		let project = this.getRawObjectFromFormObject(this.stateForm);

		this.sites = observable.array(sites, { deep: false });
		
		let uuid = this.uuid;
		let jsonData = JSON.stringify({
			project: project,
			sites: sites,
			uuid: uuid
		});

		let compressedData = LZString.compressToEncodedURIComponent(jsonData);
		let encryptedData = encrypt(compressedData)
		const response = yield savePrivateProject(encryptedData);

		this.project = observable.object(project);

		this.ui.savingProject = false;		

		if (response == null) {
			message.error("Failed to save the project. Unexpected error", 3);
			return;
		}
		if (response.error) {
			message.error(
				"Faield to save the project. Error " + response.error.message,
				3
			);
			return;
		}
		this.ui.editing = false;
		message.success("Succesfully saved the project", 3);		
	});

	@action
	addUrlList = urlList => {
		this.urlLists.push(urlList);
	};

	@action
	addContentGroup = contentGroup => {
		this.contentGroups.push(contentGroup);
	};

	@action
	showAddEditScheduleDialog = () => {
		this.ui.addEditScheduleDialogVisible = true;
	};

	@action
	hideAddEditScheduleDialog = () => {
		this.ui.addEditScheduleDialogVisible = false;
	};

	@action
	showAddLinkSetProfileDialog = () => {
		this.ui.addEditLinkSetProfileDialogVisible = true;
		this.ui.linkSetProfile = null;
	};

	@action
	showEditLinkSetProfileDialog = record => {
		this.ui.addEditLinkSetProfileDialogVisible = true;
		this.ui.linkSetProfile = record;
	};

	@action
	hideAddEditLinkSetProfileDialog = () => {
		this.ui.addEditLinkSetProfileDialogVisible = false;
		this.ui.linkSetProfile = null;
	};

	@action
	addLinkSetProfile = linkSetProfile => {
		const oldLinkSetProfile = this.ui.linkSetProfile;
		if (oldLinkSetProfile == null) {
			this.stateForm.linkSetProfiles.push(linkSetProfile);
		} else {
			for (let item of this.stateForm.linkSetProfiles) {
				if (item.linkSetId == oldLinkSetProfile.linkSetId) {
					Object.assign(item, linkSetProfile);
					break;
				}
			}
		}
		this.ui.addEditLinkSetProfileDialogVisible = false;
		this.ui.linkSetProfile = null;
	};

	@action
	deleteLinkSetProfile = linkSetId => {
		this.stateForm.linkSetProfiles = this.stateForm.linkSetProfiles.filter(
			linkSetProfile => {
				return linkSetProfile.linkSetId != linkSetId;
			}
		);
	};

	saveSchedule = flow(function*(payload) {
		this.ui.savingSchedule = true;
		const params = {
			projectId: this.project.id,
			schedule: payload
		};
		const response = yield saveSchedule(params);
		this.ui.savingSchedule = false;
		if (response == null) {
			message.error("Failed to save schedule. Unexpected error", 3);
			return;
		}
		if (response.error) {
			message.error(
				<div dangerouslySetInnerHTML={{ __html: response.error.message }} />,
				3
			);
			return;
		}

		message.success("Successfully save the schedule", 3);
		this.ui.addEditScheduleDialogVisible = false;
	});

	@action
	deleteSchedule = flow(function*() {
		this.ui.deletingSchedule = true;
		const response = yield deleteSchedule(this.project.id);
		this.ui.deletingSchedule = false;

		if (!response) {
			message.error("Failed to delete schedule. Unknown error", 3);
			return;
		}
		if (response.error) {
			message.error(
				<div dangerouslySetInnerHTML={{ _html: response.error.message }} />,
				3
			);
			return;
		}
		message.success("Successfully deleted the schedule", 3);
		this.ui.deletingSchedule = false;
	});

	@action
	resetSchedule = flow(function*() {
		this.ui.resettingSchedule = true;
		const response = yield resetSchedule(this.project.id);
		this.ui.resettingSchedule = false;

		if (!response) {
			message.error("Failed to reset schedule. Unknown error", 3);
			return;
		}
		if (response.error) {
			message.error(
				"Failed to reset schedule. Error: " + response.error.message,
				3
			);
			return;
		}
		message.success("Successfully reset the schedule", 3);
	});

	@action
	updateProjectStatus = payload => {
		// console.log("Update Project Status: " + JSON.stringify(payload));
		this.project.status = payload.status;
		this.stateForm.status = this.createFormObject(payload.status);
		this.project.lastError = payload.lastError;

		if (payload.schedule == null) {
			this.project.schedule = null;
		} else {
			this.project.schedule = observable.object(payload.schedule);
		}
	};

	@action
	updateSiteStatus = payload => {
		this.ui.updatedSite = payload;
	};

	startProject = flow(function*() {
		this.ui.startingProject = true;
		yield delay(1000);
		const response = yield startProject(this.project.id);
		// yield delay(1000);
		this.ui.startingProject = false;
		if (response == null) {
			message.error("Failed to start project. Unexpected error", 3);
			return;
		}
		if (response.error) {
			message.error(
				"Failed to start project. Error is: " + response.error.message,
				3
			);
			return;
		}
		message.success("Successfully started the project");
	});

	stopProject = flow(function*() {
		this.ui.stoppingProject = true;
		yield delay(1000);
		const response = yield stopProject(this.project.id);
		this.ui.stoppingProject = false;
		if (response == null) {
			message.error("Failed to stop project. Unexpected error", 3);
			return;
		}
		if (response.error) {
			message.error(
				"Failed to stop project. Error is: " + response.error.message,
				3
			);
			return;
		}
		message.success("Successfully stopped the project");
	});

	createFormObject = project => {
		function customizer(value, key) {
			if (!_.isObject(value) && key !== "value") {
				return {
					value: value
				};
			}

			if (_.isObject(value) && key == "indexers") {
				return {
					value: _.cloneDeep(value)
				};
			}
	
			if (_.isArray(value)) {
				return _.cloneDeep(value);
			}
		}

		let newObj = _.cloneDeepWith(toJS(project), customizer);
		return newObj;
	};

	fetchProject = flow(function*(projectId) {
		this.ui.loadingProject = true;
		const response = yield getProject({ projectId });
		if (response != null) {
			const project = response.project;
			this.project = observable.object(project);
			this.siblings = observable.array(response.siblings);
			this.campaign = observable.object(response.campaign);
			this.profiles = observable.array(response.profiles);

			this.urlLists = observable.array(response.urlLists);
			this.contentGroups = observable.array(response.contentGroups);
			this.sites = observable.array(response.sites, { deep: false });
			this.stateForm = observable.object(this.createFormObject(project));
			this.ui = observable.object(this.getInitUI());
			this.uuid = uuidv1();

			registerHandler([
				{
					path: "project/" + this.project.id,
					handler: function(err, msg) {
						if (err) {
							return;
						}

						var obj = JSON.parse(msg.body);
						if (obj.action === "projectStatusChanged") {
							this.updateProjectStatus({
								status: obj.status,
								lastError: obj.lastError,
								schedule: obj.schedule
							});
							return;
						}
						if (obj.action === "projectSaved") {
							if (obj.uuid != this.uuid) {
								window.location.reload();
							}
							return;
						}
						if (obj.action === "siteStatusChanged") {
							// console.log(obj.site);
							this.updateSiteStatus(obj.site);
							return;
						}
						if (obj.action == "verificationProgressed") {
							this.updateVerificationStatus(obj.status);
							return;
						}
					}.bind(this)
				},
				{
					path: "campaign/" + project.campaignId,
					handler: function(err, msg) {
						if (err) {
							return;
						}
						let obj = JSON.parse(msg.body);

						if (obj.action == "newUrlList") {
							let newUrlList = obj.urlList;
							this.addUrlList(newUrlList);
							return;
						}
					}.bind(this)
				},
				{
					path: "content_groups",
					handler: (err, msg) => {
						if (err) {
							return;
						}

						let obj = JSON.parse(msg.body);
						if (obj.action == "newContentGroup") {
							let contentGroup = obj.contentGroup;
							this.addContentGroup(contentGroup);
						}
					}
				}
			]);
		}
	});
}
